'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslations } from 'next-intl';
import { LayoutGrid, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

import { useShelfStats } from '../services/shelves.queries';

export function ShelvesStatCards() {
  const t = useTranslations('shelves');
  const { data, isLoading } = useShelfStats();
  const stats = data?.data;

  if (isLoading) {
    return (
      <div className='grid gap-4 sm:grid-cols-2'>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className='h-24 w-full rounded-lg' />
        ))}
      </div>
    );
  }

  const avgUtilization = stats?.avg_utilization ?? 0;
  const utilizationColor =
    avgUtilization >= 100
      ? 'text-red-600 dark:text-red-400'
      : avgUtilization >= 90
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-blue-600 dark:text-blue-400';

  return (
    <div className='grid gap-4 sm:grid-cols-2'>
      <Card>
        <CardContent className='flex items-center gap-4 px-5 py-6'>
          <div className='min-w-0 flex-1'>
            <p className='text-muted-foreground text-sm font-medium'>
              {t('stats.totalCapacity')}
            </p>
            <div className='flex items-baseline gap-2'>
              <p className={cn('text-2xl font-bold', utilizationColor)}>
                {avgUtilization.toFixed(1)}%
              </p>
              <p className='text-muted-foreground text-xs'>
                {t('stats.shelvesCount', {
                  count: (stats?.total_shelves ?? 0).toLocaleString()
                })}
              </p>
            </div>
          </div>
          <div className='rounded-xl bg-blue-50 p-3 dark:bg-blue-950'>
            <LayoutGrid className='h-5 w-5 text-blue-600 dark:text-blue-400' />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className='flex items-center gap-4 px-5 py-6'>
          <div className='min-w-0 flex-1'>
            <p className='text-muted-foreground text-sm font-medium'>
              {t('stats.activeShelves')}
            </p>
            <div className='flex items-baseline gap-2'>
              <p className='text-2xl font-bold'>
                {(stats?.active_shelves ?? 0).toLocaleString()}
              </p>
              {((stats?.full_shelves ?? 0) > 0 ||
                (stats?.maintenance_shelves ?? 0) > 0) && (
                <p className='text-muted-foreground text-xs'>
                  {t('stats.fullCount', { full: stats?.full_shelves ?? 0 })} ·{' '}
                  {t('stats.maintCount', {
                    maint: stats?.maintenance_shelves ?? 0
                  })}
                </p>
              )}
            </div>
          </div>
          <div className='rounded-xl bg-green-50 p-3 dark:bg-green-950'>
            <CheckCircle2 className='h-5 w-5 text-green-600 dark:text-green-400' />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
